import { db } from '../firebase.js';

// One read of the owner-readable users/{uid} doc → the identity + streak + coin
// fields the web Stats and Profile tabs render. (Accuracy / per-category is
// derived from the answers list instead — see Stats.jsx — to avoid depending on
// the user-doc accuracy field's scale.) All fields are optional: a brand-new
// Guest who hasn't played yet may have a sparse doc or none, so callers render
// zero-states safely.
export async function fetchUserProfile(uid) {
  if (!uid) return null;
  const snap = await db.collection('users').doc(uid).get();
  if (!snap.exists) return null;
  const d = snap.data() || {};
  return {
    displayName: typeof d.displayName === 'string' ? d.displayName : null,
    createdAt: d.createdAt && d.createdAt.toDate ? d.createdAt.toDate() : null,
    platform: typeof d.platform === 'string' ? d.platform : null,
    // The two coin numbers are NOT interchangeable. coinBalance is what you can
    // SPEND — gifts and purchases move it, and it can go down. monthlyCoinsEarned
    // is what you've EARNED from play this month: the score, and the field the
    // month leaderboard ranks on. Never render one where the other belongs.
    coinBalance: typeof d.coinBalance === 'number' ? d.coinBalance : 0,
    monthlyCoinsEarned: typeof d.monthlyCoinsEarned === 'number' ? d.monthlyCoinsEarned : 0,
    // Server-denormalized accuracy (0–100). The ghost compare face reads these;
    // Stats.jsx still derives its own from the answers list on purpose.
    dailyAccuracy: typeof d.dailyAccuracy === 'number' ? d.dailyAccuracy : 0,
    overallAccuracy: typeof d.overallAccuracy === 'number' ? d.overallAccuracy : 0,
    currentStreak: typeof d.currentStreak === 'number' ? d.currentStreak : 0,
    longestStreak: typeof d.longestStreak === 'number' ? d.longestStreak : 0,
    // CROWD BEATS, all time — right while the crowd was wrong. Server-true
    // (resolveQuestion increments it) and therefore the ONLY honest all-time
    // number here: the web answers read is capped at 150 rows, so a
    // client-derived "all time" would visibly shrink as old wins slid out.
    crowdBeatsLifetime:
      typeof d.crowdBeatsLifetime === 'number' ? d.crowdBeatsLifetime : 0,
    // ET-date string of the user's last awarded daily submission. The web Play
    // screen polls this to know when the async submission award has landed
    // (it's stamped by `awardSubmissionPoints` in the same txn as the streak +
    // coin increments) before it computes the earned-this-submission summary.
    lastSubmissionPointsDate:
      typeof d.lastSubmissionPointsDate === 'string' ? d.lastSubmissionPointsDate : null,
    // Today's question-share-bonus bucket { day (playDayString), qids[] } — the
    // server's per-question +6 record (webBonuses.ts). The Play screen reads it
    // to restore the durable "+coins earned" header + the picker's gold
    // "already shared" gradients after a reload (session state alone resets).
    questionShareBonusDay:
      d.questionShareBonusDay && typeof d.questionShareBonusDay === 'object'
        ? {
            day:
              typeof d.questionShareBonusDay.day === 'string'
                ? d.questionShareBonusDay.day
                : null,
            qids: Array.isArray(d.questionShareBonusDay.qids)
              ? d.questionShareBonusDay.qids.filter((x) => typeof x === 'string')
              : [],
          }
        : null,
    // One-shot server gate for the web first-submission bonus ballot (the
    // founder-twist auth-gated 12-question round). Stamped server-side inside
    // `claimWebBonusBallot`; the web Play screen reads it to decide whether to
    // offer the ballot at all (true ⇒ already done, never re-offer).
    hasSeenFirstSubmissionBonusWeb: d.hasSeenFirstSubmissionBonusWeb === true,
    // For AppShell's "app account" nudge + the cross-device Results badge —
    // folded in here so AppShell can reuse this cached profile instead of doing
    // its own duplicate users/{uid} read (audit X2).
    appDownloadBonusAt: !!d.appDownloadBonusAt,
    lastResultsViewedAtMillis: d.lastResultsViewedAt?.toMillis?.() ?? 0,
    // Equipped cosmetics map ({ avatar, frame, banner, tagline, sheetTheme }) —
    // drives the web Avatar render + the customization card's equipped state.
    // Server-written (via the equip callables); read-only here.
    equippedCosmetics:
      d.equippedCosmetics && typeof d.equippedCosmetics === 'object'
        ? d.equippedCosmetics
        : {},
    // Back Sections (FP-4): the categories this user follows, in PRIORITY order
    // — top pick first. Empty = never chosen, which is what the web post-six
    // flow uses to decide whether to show the interest picker before offering
    // any section questions (Tom 2026-07-28).
    sectionPreferences: Array.isArray(d.sectionPreferences)
      ? d.sectionPreferences.filter((x) => typeof x === 'string')
      : [],
    // Server-only §3f dogfood flag. Read here for completeness, but NEVER as
    // the visibility gate on its own: it is half of iOS's
    // `sections_enabled || sectionsFounder` OR, and the other half lives in
    // admin-only config. Use `fetchSectionsGate()` (data/sections.js).
    sectionsFounder: d.sectionsFounder === true,
    // Follow / Creator mode (docs/follow-mode-proposal.md). Server-written; the
    // Profile creator card reads them to show the follow link (approved) / an
    // "under review" state / the apply entry.
    isCreator: d.isCreator === true,
    creatorHandle: typeof d.creatorHandle === 'string' ? d.creatorHandle : '',
    creatorVerified: d.creatorVerified === true,
    followerCount: typeof d.followerCount === 'number' ? d.followerCount : 0,
    creatorApplicationStatus:
      typeof d.creatorApplicationStatus === 'string' ? d.creatorApplicationStatus : 'none',
    // Creator-editable public profile (bio / social links / website) — the
    // Edit-creator-profile modal prefills from these; setCreatorProfile writes
    // them. Social keys are LOCKSTEP with follow.ts CREATOR_SOCIAL_KEYS.
    creatorBio: typeof d.creatorBio === 'string' ? d.creatorBio : '',
    creatorSocials:
      d.creatorSocials && typeof d.creatorSocials === 'object' ? d.creatorSocials : {},
    creatorWebsite: typeof d.creatorWebsite === 'string' ? d.creatorWebsite : '',
  };
}

/**
 * Save the user's back-section interests (FP-4) — the SELECTED categories in
 * priority order, top pick first. Mirrors iOS
 * `FirestoreService.updateSectionPreferences`.
 *
 * `sectionPreferences` is already on the firestore.rules update allowlist
 * (line 250), so this is permitted — but the payload must carry that field and
 * NOTHING else: the rule is `affectedKeys().hasOnly([...])`, so one stray field
 * rejects the entire atomic write (root rule 9).
 *
 * Best-effort, exactly like iOS: the picker is skippable and non-critical, so a
 * write failure is logged rather than surfaced. It must not block the user from
 * reaching the questions.
 */
export async function saveSectionPreferences(uid, categories) {
  if (!uid || !Array.isArray(categories)) return false;
  try {
    await db
      .collection('users')
      .doc(uid)
      .update({ sectionPreferences: categories.filter((c) => typeof c === 'string') });
    return true;
  } catch (e) {
    console.error('[sections] saveSectionPreferences failed:', e);
    return false;
  }
}
